import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Users, CheckCircle, Clock, XCircle, Edit, UserPlus, Settings } from 'lucide-react';
import { showToast } from '../utils/toast';
import AddUserModal from '../components/AddUserModal';
import EditUserModal from '../components/EditUserModal';
import ConfirmationModal from '../components/ConfirmationModal';

const planNames = {
    'startup': 'Startup',
    'starter': 'Starter',
    'advanced': 'Advanced',
    'custom': 'Personalizado'
};

function UsersPage() {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    // Estados para o modal de confirmação
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalProps, setConfirmModalProps] = useState({});

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const functions = getFunctions();
            const getAllUsers = httpsCallable(functions, 'get_all_msp_users');
            const result = await getAllUsers();
            const sortedUsers = result.data.users.sort((a, b) =>
                (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
            );
            setUsers(sortedUsers);
        } catch (error) {
            console.error("Erro ao buscar usuários:", error);
            showToast('error', `Não foi possível carregar os usuários: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleOpenEditModal = (user) => {
        setEditingUser(user);
        setShowEditModal(true);
    };

    const handleUpdateStatus = (targetUid, currentStatus) => {
        const statusOptions = {
            'pending_approval': 'active',
            'active': 'disabled',
            'disabled': 'active'
        };
        const newStatus = statusOptions[currentStatus];
        if (!newStatus) return;

        const performUpdate = async () => {
            setShowConfirmModal(false);
            try {
                const functions = getFunctions();
                const updateUserStatus = httpsCallable(functions, 'update_msp_user_status');
                await updateUserStatus({ targetUid, newStatus });
                showToast('success', 'O status do usuário foi atualizado.');
                fetchUsers(); // Atualiza a lista de usuários
            } catch (error) {
                showToast('error', `Não foi possível atualizar o usuário: ${error.message}`);
            }
        };

        setConfirmModalProps({
            title: `Mudar status para "${newStatus}"?`,
            body: "Você está prestes a alterar o status do usuário.",
            confirmText: "Sim, alterar!",
            onConfirm: performUpdate,
        });

        setShowConfirmModal(true);
    };

    const getStatusBadge = (status) => {
        const styles = {
            'active': { icon: <CheckCircle size={16} />, class: 'text-bg-success' },
            'pending_approval': { icon: <Clock size={16} />, class: 'text-bg-warning' },
            'disabled': { icon: <XCircle size={16} />, class: 'text-bg-secondary' }
        };
        const style = styles[status] || { icon: null, class: 'text-bg-light' };
        return (
            <span className={`badge ${style.class} d-inline-flex align-items-center`}>
                {style.icon}
                <span className="ms-1 text-capitalize">{status.replace(/_/g, ' ')}</span>
            </span>
        );
    };

    return (
        <>
            <AddUserModal
                show={showAddModal}
                onClose={() => setShowAddModal(false)}
                onUserAdded={fetchUsers}
            />
            <EditUserModal
                show={showEditModal}
                onClose={() => setShowEditModal(false)}
                editingUser={editingUser}
                onUserEdited={fetchUsers}
            />
            <ConfirmationModal
                show={showConfirmModal}
                onHide={() => setShowConfirmModal(false)}
                {...confirmModalProps}
            />

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="h3 d-flex align-items-center"><Users className="me-3" />Gerenciamento de Usuários</h1>
                <button className="btn btn-primary d-flex align-items-center" onClick={() => setShowAddModal(true)}>
                    <UserPlus size={16} className="me-2" />
                    Adicionar Usuário
                </button>
            </div>
            <div className="card shadow-sm">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th scope="col" className="ps-4">E-mail / Empresa</th>
                                    <th scope="col">Plano</th>
                                    <th scope="col">Limite de Tenants</th>
                                    <th scope="col">Status</th>
                                    <th scope="col">Data de Cadastro</th>
                                    <th scope="col" className="text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan="6" className="text-center p-5">Carregando usuários...</td></tr>
                                ) : (
                                    users.map(user => (
                                        <tr key={user.uid}>
                                            <td className="ps-4 align-middle">
                                                <div className="fw-medium">{user.contactEmail}</div>
                                                <div className="small text-muted">{user.companyName !== user.contactEmail ? user.companyName : ''}</div>
                                            </td>
                                            <td className="align-middle text-capitalize">
                                                {planNames[user.plan] || user.plan}
                                            </td>
                                            <td className="align-middle fw-medium text-center">
                                                {user.maxTenants ?? '—'}
                                            </td>
                                            <td className="align-middle">{getStatusBadge(user.status)}</td>
                                            <td className="align-middle small">
                                                {user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                                            </td>
                                            <td className="align-middle text-center">
                                                <div className="btn-group btn-group-sm" role="group">
                                                    <button
                                                        className="btn btn-outline-secondary d-flex align-items-center"
                                                        onClick={() => handleOpenEditModal(user)}
                                                    >
                                                        <Edit size={14} className="me-1" />
                                                        Editar Plano
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-secondary d-flex align-items-center"
                                                        onClick={() => handleUpdateStatus(user.uid, user.status)}
                                                    >
                                                        <Settings size={14} className="me-1" />
                                                        Alterar Status
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                {!isLoading && users.length === 0 && (
                                    <tr><td colSpan="6" className="text-center p-5">Nenhum usuário encontrado.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </>
    );
}

export default UsersPage;